import * as C from "../engine/constants";
import { mulberry32, type Rng } from "../engine/rng";
import { vibrateHits, prefersReducedMotion, canVibrate } from "../engine/haptics";
import type { CrushAudio } from "../engine/audio";
import { ShellField, HOLE_W, HOLE_H, type Plate3D } from "./shell";
import { FIELD_H, DIST_SCALE, dirToField, type FieldResult } from "./plateField";
import { ImprintMap, type StampOpts } from "./imprint";
import { crackOnBeforeCompile, makeCrackUniforms, makeAoShapeMaterial, type CrackUniforms, type makeDummyTextures } from "./crackShader";
import { StickyDrag } from "./sticky";
import type { FieldTicket } from "./fieldPool";
import type { ThreeNS } from "./three";

export type V3 = [number, number, number];

export type Press3D = {
  pointerId: number;
  type: string;
  pressure: number;
  x: number; y: number; sx: number; sy: number; lastX: number; lastY: number;
  downAt: number; moved: number; isDrag: boolean; speed: number; lastMoveAt: number;
  /** finger velocity on screen (CSS px/s), for rolling the ball under a dragging finger and for flicks */
  vx: number; vy: number;
  /** the touched point on the ball: unit direction from its centre, in world space (camera facing +z) */
  dirW: V3;
  /** the same point in the ball's own, rotating frame (everything on the ball works in this frame) */
  dir: V3;
  /** the current damage stroke: where it is centred (ball frame) and when it started */
  strokeDir: V3;
  strokeAt: number;
  nextFlakeAt: number;
};

export type Env3D = {
  audio: CrushAudio;
  pxToWorld: number;
  /** wax dust from a world-space point on the ball (unit direction from its centre) */
  spawnCrumbs: (b: Ball3D, dirW: V3, n: number) => void;
  /** `n` more plates have just been crushed into fragments (the "뿌순 개수" counter) */
  onCrushed: (b: Ball3D, n: number) => void;
};

/**
 * What every ball shares: the static render mesh (a fine sphere; the vertex shader gives it its shape) and the
 * coarse shape grid the plastic memory lives on (restR per grid vertex, uploaded as a small texture).
 */
export type SharedGeo = {
  /** fine unit sphere with a colour attribute of ones; never changes, so all balls share it */
  template: any;
  /** unit directions of the shape grid's vertices (SphereGeometry(1, segW, segH) layout) */
  dirs: Float32Array;
  nv: number;
  /** the round ball's volume measure Σ sinθ over the grid (seam column left out): the bake rescales to it */
  v0: number;
  dummies: ReturnType<typeof makeDummyTextures>;
  /** wax micro-detail normal map (a flat placeholder until the background thread delivers it) */
  detail: any;
  segW: number;
  segH: number;
};

// crack widths: half-width of the clay gap, in crack-field texels (π/512 rad each)
const GAP_CRACK = 1.1, GAP_OPEN = 2.1, GAP_LOOSE = 3.0;
/** how soft the ball is under a plate at each damage level (0 = hard intact wax … 1 = clay with crumbs in it) */
const SOFT = [0, 0.04, 0.14, 0.3, 0.5, 0.75, 0.95];
/** past DMG_GONE a plate is crushed; by DMG_GONE + CRUSH_SPAN its fragments are spread and pressed right in */
const CRUSH_SPAN = 0.9, DMG_CAP = 2.2;
/** a release's fingerprint is pressed in over this many steps (~0.1 s) */
const PRINT_STEPS = 6;
/** finger-print softness per damage level: hard or barely cracked wax keeps no print */
const IMP_SOFT = [0, 0, 0, 0.1, 0.2, 0.9, 1]; // rigid open or loose slabs take almost no print; clay and fragments do

/** The static render mesh: a unit sphere with a colour attribute of ones (the vertex shader does the rest). */
export function renderTemplate(THREE: ThreeNS, segW: number, segH: number) {
  const template = new THREE.SphereGeometry(1, segW, segH);
  const n = template.attributes.position.count;
  template.setAttribute("color", new THREE.BufferAttribute(new Float32Array(n * 3).fill(1), 3));
  return template;
}

export function buildSharedGeo(THREE: ThreeNS, dummies: ReturnType<typeof makeDummyTextures>, detail: any, seg: [number, number] = [96, 64], render: [number, number] = [224, 112]): SharedGeo {
  const [SEG_W, SEG_H] = seg;
  const template = renderTemplate(THREE, render[0], render[1]);
  // the shape grid: three's SphereGeometry vertex layout, rows from the north pole, the seam column doubled
  const nv = (SEG_W + 1) * (SEG_H + 1);
  const dirs = new Float32Array(nv * 3);
  for (let iy = 0; iy <= SEG_H; iy++) {
    const th = (iy / SEG_H) * Math.PI;
    for (let ix = 0; ix <= SEG_W; ix++) {
      const ph = (ix / SEG_W) * Math.PI * 2, v = (iy * (SEG_W + 1) + ix) * 3;
      dirs[v] = -Math.cos(ph) * Math.sin(th); dirs[v + 1] = Math.cos(th); dirs[v + 2] = Math.sin(ph) * Math.sin(th);
    }
  }
  let v0 = 0;
  for (let v = 0; v < nv; v++) if (v % (SEG_W + 1) !== SEG_W) v0 += Math.sqrt(Math.max(0, 1 - dirs[v * 3 + 1] * dirs[v * 3 + 1]));
  return { template, dirs, nv, v0, dummies, detail, segW: SEG_W, segH: SEG_H };
}

const easeOut = (u: number) => 1 - Math.pow(1 - Math.max(0, Math.min(1, u)), 3);
export const angle = (a: ArrayLike<number>, b: ArrayLike<number>) => Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2])));

type Near = { p: Plate3D; w: number };
type Events = { hair: number; crack: number; open: number; loose: number; crushed: number };

export class Ball3D {
  seed: number;
  rng: Rng;
  variant: C.ShellVariant;
  pitchMul: number;
  R: number; // world units
  x = 0; y = 0; vx = 0; vy = 0;
  mesh: any;
  material: any;
  /** the ball's material for the AO G-buffer: its shape on a normal material */
  aoMaterial: any;
  shell: ShellField;
  private texColor: any;
  private texIds: any = null; private texDist: any = null; private texFine: any = null; private texState: any; private texFront: any; private texHole: any = null;
  private texRest: any; private restHalf: Uint16Array; private texImprint: any;
  /** the band of finger-mark rows to re-upload (a press touches a few hundred rows, not the whole map) */
  private imBox: any; private imPos: any;
  /** finger marks in the clay */
  imprint: ImprintMap;
  private crack: CrackUniforms;
  /** plastic memory: radius per shape-grid vertex (1 = round); the vertex shader reads it as uRestR */
  restR: Float32Array;
  /** largest rest radius, in R (≥ 1): the silhouette never reaches past it whichever way the ball has turned */
  extent = 1;
  sleeping = true;
  settle = 0;
  fieldTicket: FieldTicket | null = null;
  press: Press3D | null = null;
  /** 0 intact, 1 hairlines, 2 cracked, 3 broken through (plates open, loose or crushed), 4 mostly crushed */
  stage: 0 | 1 | 2 | 3 | 4 = 0;
  /** something has broken through somewhere (the sticky necks need open shell to pull on) */
  popped = false;
  pressCount = 0; lastReleaseAt = -1e9;
  D = 0; Dt = 0; sigma = C.SIGMA_SHELL; sPar = 1; sParT = 1; sPerp = 1;
  dent: V3 = [0, 0, 1];
  sq: V3 = [0, 0, 1];
  ooze = 0;
  punchT = 0;
  /** the press's softness under the finger (0 hard wax … 1 bare clay), drives the dent and the sounds */
  soft = 0;
  /** the rest of the ball swelling while the finger pushes in (fraction of R; relaxes after release) */
  swell = 0;
  /** the clay a moving finger drives ahead of itself: tangent direction of its travel (ball frame) × strength 0..1 */
  push: V3 = [0, 0, 0];
  /** how flat-bottomed the dent is: 0 a soft gaussian in hard wax … 1 a fingertip pad sunk into clay */
  flat = 0;
  /** the squash axis wanted this frame (ball frame), eased into sq so a quick re-press never snaps the shape */
  private sqT: V3 = [0, 0, 1];
  /** the dent's travel since the last drag bake (rad) */
  private dragRun = 0;
  /** imprint softness per plate id (IMP_SOFT of its level), refreshed when a stamp needs it */
  private impSoft = new Float32Array(256);
  /** the fingerprint left by the last release, pressed in over a few frames as the dent springs back */
  private pendingPrint: { c: V3; radius: number; depth: number; left: number } | null = null;
  crushedCount = 0;
  crackedCount = 0;
  disposed = false;
  pace: C.Pace;
  sticky: StickyDrag;
  /** angular velocity, world space (rad/s); idle balls turn slowly about a wandering axis */
  spin: any; private idleAxis: any; private idleT = 2;
  private near: Near[] = [];
  private nextCrackSound = 0; private nextHairSound = 0;
  private fp0 = new Float32Array(3); private fp1 = new Float32Array(3); private fp2 = new Float32Array(3); // surfaceFrame scratch
  private tq: any; private tv: any; private dq: any;

  private THREE: ThreeNS;
  private geo: SharedGeo;
  id: number;

  constructor(THREE: ThreeNS, geo: SharedGeo, id: number, R: number, variant: C.ShellVariant, pace: C.Pace = C.PACE) {
    this.THREE = THREE; this.geo = geo; this.id = id;
    this.seed = Math.floor(Math.random() * 1e9);
    this.rng = mulberry32(this.seed);
    this.variant = variant;
    this.pace = pace;
    this.pitchMul = 0.85 + this.rng() * 0.35;
    this.R = R;
    this.restR = new Float32Array(geo.nv).fill(1);
    this.shell = new ShellField(variant, this.rng, pace.seeds, pace.res);
    this.shell.tau = pace.openTau; this.shell.hairS = pace.hairS;
    const T = THREE;
    this.texColor = new T.CanvasTexture(this.shell.color); this.texColor.colorSpace = T.SRGBColorSpace;
    this.texColor.wrapS = T.RepeatWrapping; this.texColor.anisotropy = 4;
    this.texState = new T.DataTexture(this.shell.state, this.shell.state.length / 4, 1, T.RGBAFormat, T.UnsignedByteType);
    this.texState.needsUpdate = true;
    this.texFront = new T.DataTexture(this.shell.front, this.shell.front.length / 4, 1, T.RGBAFormat, T.UnsignedByteType);
    this.texFront.needsUpdate = true;
    this.crack = makeCrackUniforms(T, geo.dummies, variant);
    this.crack.uPlateState.value = this.texState;
    this.crack.uPlateFront.value = this.texFront;
    // the plastic shape as a small half-float texture, and the finger marks
    this.restHalf = new Uint16Array(geo.nv); // stores r − 1 (half floats are far finer near 0 than near 1)
    this.texRest = new T.DataTexture(this.restHalf, geo.segW + 1, geo.segH + 1, T.RedFormat, T.HalfFloatType);
    this.texRest.magFilter = this.texRest.minFilter = T.LinearFilter; this.texRest.generateMipmaps = false; this.texRest.needsUpdate = true;
    this.imprint = new ImprintMap(this.seed, pace.imp[0], pace.imp[1]);
    this.texImprint = new T.DataTexture(this.imprint.data, this.imprint.W, this.imprint.H, T.RGBAFormat, T.UnsignedByteType);
    this.imBox = new T.Box2(new T.Vector2(), new T.Vector2()); this.imPos = new T.Vector2();
    // mipmapped: the vertex stage reads a blurred level (the mesh is coarser than the map), small balls a filtered one
    this.texImprint.magFilter = T.LinearFilter; this.texImprint.minFilter = T.LinearMipmapLinearFilter; this.texImprint.wrapS = T.RepeatWrapping;
    this.texImprint.generateMipmaps = true; this.texImprint.needsUpdate = true;
    const u = this.crack;
    u.uShapeOn.value = 1; u.uRestR.value = this.texRest; u.uImprint.value = this.texImprint;
    (u.uImpSize.value as any).set(this.imprint.W, this.imprint.H);
    (u.uRestSize.value as any).set(geo.segW, geo.segH);
    this.material = this.makeMaterial(this.texColor, this.crack);
    this.mesh = new T.Mesh(geo.template, this.material);
    this.mesh.scale.setScalar(R);
    this.mesh.frustumCulled = false;
    // the desktop AO pass draws the ball with this instead (Engine3D.startPost swaps it in for the G-buffer render)
    this.aoMaterial = makeAoShapeMaterial(T, this.crack);
    this.mesh.aoMaterial = this.aoMaterial;
    this.tq = new T.Quaternion(); this.dq = new T.Quaternion(); this.tv = new T.Vector3();
    this.spin = new T.Vector3();
    // a random start orientation and a mostly upright idle axis, so the balls do not all turn alike
    this.mesh.quaternion.setFromEuler(new T.Euler(this.rng() * 6.28, this.rng() * 6.28, this.rng() * 6.28));
    this.idleAxis = new T.Vector3(this.rng() - 0.5, 1.2, (this.rng() - 0.5) * 0.6).normalize();
    if (this.rng() < 0.5) this.idleAxis.negate();
    this.setRadius(R);
    this.sticky = new StickyDrag(THREE, this);
    this.deform();
  }

  /**
   * The shell material: dipped wax, not lacquer — one satin layer (no clearcoat): a soft highlight with the dip
   * texture in it (the detail map as normalMap), the gloss varying over the surface (its alpha), a pale grazing bloom,
   * and light that goes into the coat and comes back out deeper in colour past the shadow line (crackShader.ts,
   * RE_Direct_Wax). Balls and peeled chips share this exact setup, so they share one compiled program.
   */
  private makeMaterial(map: any, crack: CrackUniforms) {
    const T = this.THREE;
    const m = new T.MeshPhysicalMaterial({
      map, color: 0xffffff, metalness: 0, roughness: 0.4, ior: 1.45, specularIntensity: 0.85, envMapIntensity: 1,
      normalMap: this.geo.detail, normalScale: new T.Vector2(0.6, 0.6),
      sheen: 0.3, sheenRoughness: 0.55, sheenColor: new T.Color(this.variant.base).lerp(new T.Color(0xffffff), 0.2),
      transparent: true, // chips fade; the same setup everywhere avoids a shader recompile mid-game
    });
    m.vertexColors = true;
    m.crackUniforms = crack;
    m.onBeforeCompile = crackOnBeforeCompile;
    return m;
  }

  /** Material for a chip or flake top: the ball's program with its own paint and cracks switched off. */
  makeChipMaterial(map: any) {
    map.colorSpace = this.THREE.SRGBColorSpace;
    return this.makeMaterial(map, makeCrackUniforms(this.THREE, this.geo.dummies, this.variant));
  }

  /** The shell paint (equirect, sRGB): flakes that break off show it on their outer face. */
  get paint() { return this.texColor; }

  setRadius(R: number) {
    this.R = R;
    this.mesh.scale.setScalar(R * (1 + 0.025 * Math.sin(Math.PI * this.punchT)));
    this.crack.uTexelWorld.value = (R * Math.PI) / FIELD_H; // one crack-field distance unit (a 1024×512 texel) in world units
  }

  /** Uploads the paint texture now (idle time), so the ball's first frame on screen uploads nothing big. */
  warm(renderer: any) { renderer.initTexture?.(this.texColor); }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.fieldTicket?.cancel();
    this.sticky.dispose();
    this.material.dispose(); this.aoMaterial.dispose(); // the mesh's geometry is shared by every ball
    this.texColor.dispose(); this.texState.dispose(); this.texFront.dispose(); this.texRest.dispose(); this.texImprint.dispose();
    this.texIds?.dispose(); this.texDist?.dispose(); this.texFine?.dispose(); this.texHole?.dispose();
  }

  // ---------- frames: the ball's own (rotating) frame vs world ----------

  /** World-space direction → the ball's frame. */
  toLocal(w: ArrayLike<number>, out: V3 = [0, 0, 0]): V3 {
    this.tq.copy(this.mesh.quaternion).invert();
    this.tv.set(w[0], w[1], w[2]).applyQuaternion(this.tq);
    out[0] = this.tv.x; out[1] = this.tv.y; out[2] = this.tv.z;
    return out;
  }

  /** The ball's frame → world-space direction. */
  toWorld(l: ArrayLike<number>, out: V3 = [0, 0, 0]): V3 {
    this.tv.set(l[0], l[1], l[2]).applyQuaternion(this.mesh.quaternion);
    out[0] = this.tv.x; out[1] = this.tv.y; out[2] = this.tv.z;
    return out;
  }

  /** How much a point of the ball (its own frame) faces the camera: world z of the direction. */
  facing(l: ArrayLike<number>) {
    this.tv.set(l[0], l[1], l[2]).applyQuaternion(this.mesh.quaternion);
    return this.tv.z;
  }

  /**
   * Turns the ball: held still under a finger (a dragging finger rolls it a little), otherwise slowly about a
   * wandering idle axis so every side comes round. Runs every step, even while the ball sleeps.
   */
  spinStep(dt: number, pxToWorld: number) {
    const p = this.press, s = this.spin;
    if (p) {
      s.multiplyScalar(Math.exp(-dt / 0.05));
      if (p.isDrag && performance.now() - p.lastMoveAt < 80) {
        // rolling under the finger: ω = ẑ × v / R, at a third of a no-slip roll so the finger still slides a bit;
        // sunk into bare clay it mostly scrapes (the clay gives way instead of carrying the ball round)
        const k = (0.33 * (1 - 0.75 * this.soft) * pxToWorld) / this.R;
        s.x += -(-p.vy) * k * 0.25; s.y += p.vx * k * 0.25; // (screen y points down)
      }
      this.idleT = 0;
    } else {
      this.idleT += dt;
      // only once it has been left alone for a while, and then gently: what you just broke stays where you broke it
      const ramp = Math.max(0, Math.min(1, (this.idleT - 2.5) / 2));
      const ax = this.idleAxis;
      // the idle axis drifts a little, so the ball never shows the same band for long
      ax.x += (this.rng() - 0.5) * 0.02; ax.z += (this.rng() - 0.5) * 0.02; ax.normalize();
      const w = this.pace.idleSpin * ramp, k = 1 - Math.exp(-dt / 1.2);
      s.x += (ax.x * w - s.x) * k; s.y += (ax.y * w - s.y) * k; s.z += (ax.z * w - s.z) * k;
    }
    const a = s.length() * dt;
    if (a > 1e-7) {
      this.tv.copy(s).normalize();
      this.dq.setFromAxisAngle(this.tv, a);
      this.mesh.quaternion.premultiply(this.dq);
      // a finger resting on a turning ball stays on the same spot of the screen, so its point on the ball moves
      if (p) this.toLocal(p.dirW, p.dir);
    }
  }

  /** A flick: the finger left the ball while moving, so it keeps rolling that way for a while. */
  flick(vxPx: number, vyPx: number, pxToWorld: number) {
    const k = (0.45 * pxToWorld) / this.R;
    this.spin.x += vyPx * k; this.spin.y += vxPx * k;
    const m = this.spin.length();
    if (m > 5) this.spin.multiplyScalar(5 / m);
  }

  // ---------- field, state upload, clay mask ----------

  /** The background thread delivered this ball's crack field. Runs at idle time; uploads the textures right away. */
  applyField(r: FieldResult, renderer: any) {
    const T = this.THREE;
    this.shell.applyField(r);
    const W = r.w, H = r.h;
    const ids = new T.DataTexture(r.ids, W, H, T.RGFormat, T.UnsignedByteType);
    ids.magFilter = ids.minFilter = T.NearestFilter; ids.wrapS = T.RepeatWrapping; ids.generateMipmaps = false; ids.needsUpdate = true;
    const dist = new T.DataTexture(r.dist, W, H, T.RedFormat, T.UnsignedByteType);
    dist.magFilter = dist.minFilter = T.LinearFilter; dist.wrapS = T.RepeatWrapping; dist.generateMipmaps = false; dist.needsUpdate = true;
    renderer.initTexture?.(ids); renderer.initTexture?.(dist);
    this.texIds = ids; this.texDist = dist;
    this.crack.uFieldId.value = ids; this.crack.uFieldDist.value = dist;
    if (r.fine) {
      const fine = new T.DataTexture(r.fine, W, H, T.RGFormat, T.UnsignedByteType);
      fine.magFilter = fine.minFilter = T.LinearFilter; fine.wrapS = T.RepeatWrapping; fine.generateMipmaps = false; fine.needsUpdate = true;
      renderer.initTexture?.(fine);
      this.texFine = fine; this.crack.uFieldFine.value = fine;
    }
    this.crack.uCrackOn.value = 1;
    this.shell.prepareSmall();
    if (this.press) this.beginStroke(this.press, performance.now());
  }

  /** Uploads plate state and holes when they changed (the finger marks go up once per frame: uploadMarks). */
  syncShell() {
    const s = this.shell;
    if (s.stateDirty) { this.texState.needsUpdate = true; this.texFront.needsUpdate = true; s.stateDirty = false; }
    if (s.holeDirty && s.hole) {
      if (!this.texHole) {
        const T = this.THREE;
        this.texHole = new T.DataTexture(s.hole, HOLE_W, HOLE_H, T.RedFormat, T.UnsignedByteType);
        this.texHole.magFilter = this.texHole.minFilter = T.LinearFilter; this.texHole.wrapS = T.RepeatWrapping; this.texHole.generateMipmaps = false;
        this.crack.uHole.value = this.texHole;
      }
      this.texHole.needsUpdate = true; s.holeDirty = false;
    }
    this.crack.uOoze.value = this.ooze;
    this.crack.uMud.value = s.mud;
  }

  /**
   * Uploads the finger marks, once per frame before rendering (however many physics steps stamped). The map is up to
   * 19 MB: only the rows the stamps wrote go up (copyTextureToTexture onto itself is a texSubImage2D of that band),
   * unless the texture has not been uploaded whole yet.
   */
  uploadMarks(renderer: any) {
    const tex = this.texImprint, im = this.imprint;
    if (!im.dirty) return;
    im.dirty = false;
    const props = renderer?.copyTextureToTexture ? renderer.properties?.get?.(tex) : null;
    if (!props || props.__version !== tex.version || im.dirtyY1 - im.dirtyY0 > im.H * 0.6) { tex.needsUpdate = true; return; }
    this.imBox.min.set(0, im.dirtyY0); this.imBox.max.set(im.W, im.dirtyY1); this.imPos.set(0, im.dirtyY0);
    renderer.copyTextureToTexture(tex, tex, this.imBox, this.imPos);
  }

  // ---------- shape ----------

  /** How much clay the press pushes up just outside the dent (relative to the dent): more where the clay is bare. */
  private rimK() { return 0.12 + 0.25 * this.soft; }

  /**
   * The live shape is the vertex shader's job (crackShader.ts, shRadius / VERT_SHAPE): here it only gets this
   * frame's dent, squash and swell. Nothing per vertex runs on the CPU.
   */
  deform() {
    const u = this.crack;
    (u.uDent.value as any).set(this.dent[0], this.dent[1], this.dent[2]);
    (u.uSq.value as any).set(this.sq[0], this.sq[1], this.sq[2]);
    u.uD.value = this.D; u.uSigma.value = this.sigma; u.uSPar.value = this.sPar; u.uSPerp.value = this.sPerp;
    u.uDark.value = 0.4 * Math.min(1, this.D / 0.12);
    u.uRim.value = this.rimK(); u.uVol.value = this.swell;
    (u.uPush.value as any).set(this.push[0], this.push[1], this.push[2]); u.uFlat.value = this.flat;
    const g = this.mesh.geometry.parameters; // the shared render mesh (rebuilt when the screen size changes)
    (u.uMeshCell.value as any).set(1 / g.widthSegments, 1 / g.heightSegments);
    // the vertex stage's finger marks blurred over about one mesh cell (finer would alias between the vertices)
    u.uImpLod.value = Math.max(0, Math.log2(this.imprint.W / g.widthSegments) - 0.2);
  }

  /**
   * The live dent's two terms at unit (ax, ay, az), the CPU copy of the vertex shader's shDent: tg, the fingertip
   * pad (0..1), and tl, the lip of clay pushed up round it, heaped ahead of a moving finger.
   */
  private tg = 0; private tl = 0;
  private dentTerms(ax: number, ay: number, az: number) {
    const d = this.dent, cd = Math.max(-1, Math.min(1, ax * d[0] + ay * d[1] + az * d[2]));
    const th = Math.acos(cd), s = Math.max(1e-3, this.sigma), x = th / s;
    this.tg = Math.exp(-0.5 * x * x) * (1 - this.flat) + Math.exp(-0.5 * Math.pow(x, 2.8)) * this.flat;
    const q = (th - C.RIM_RING * s) / (C.RIM_WIDTH * s);
    const tx = ax - d[0] * cd, ty = ay - d[1] * cd, tz = az - d[2] * cd, tlen = Math.hypot(tx, ty, tz);
    const p = this.push, ahead = tlen > 1e-4 ? (tx * p[0] + ty * p[1] + tz * p[2]) / tlen : 0;
    this.tl = Math.exp(-0.5 * q * q) * (1 + 1.4 * ahead);
  }

  /** The smooth radius along unit `a` (no relief): restR bilinear on the shape grid, the dent, its ring and swell. Same maths as shRadius. */
  /** The plastic memory along unit `a`: restR bilinear on the shape grid (what the shader samples). */
  private restAt(a: ArrayLike<number>) {
    let phi = Math.atan2(a[2], -a[0]); if (phi < 0) phi += Math.PI * 2;
    const SEG_W = this.geo.segW, SEG_H = this.geo.segH;
    const fx = (phi / (Math.PI * 2)) * SEG_W, fy = (Math.acos(Math.max(-1, Math.min(1, a[1]))) / Math.PI) * SEG_H;
    const ix = Math.min(SEG_W - 1, Math.floor(fx)), iy = Math.min(SEG_H - 1, Math.floor(fy));
    const wx = fx - ix, wy = fy - iy, i00 = iy * (SEG_W + 1) + ix, i10 = i00 + SEG_W + 1, R = this.restR;
    return (R[i00] + (R[i00 + 1] - R[i00]) * wx) * (1 - wy) + (R[i10] + (R[i10 + 1] - R[i10]) * wx) * wy;
  }

  private radiusAt(a: ArrayLike<number>) {
    const ax = a[0], ay = a[1], az = a[2];
    const r = this.restAt(a);
    const cs = Math.max(-1, Math.min(1, ax * this.sq[0] + ay * this.sq[1] + az * this.sq[2]));
    let f = 1 + this.swell * (1 - 0.5 * cs);
    if (this.D > 0) {
      this.dentTerms(ax, ay, az);
      f += -this.D * this.tg + this.D * this.rimK() * this.tl;
    }
    return r * f;
  }

  /**
   * A plate's own top (fraction of R): standing proud and tipped as a rigid wedge (once its crack front has
   * `reached` this point), or pressed in. As shReliefAt.
   */
  private plateTop(p: Plate3D, a: ArrayLike<number>, reached: boolean) {
    const c = p.dir, o = p.origin;
    let wx = c[0] - o[0], wy = c[1] - o[1], wz = c[2] - o[2];
    const k = wx * c[0] + wy * c[1] + wz * c[2]; wx -= c[0] * k; wy -= c[1] * k; wz -= c[2] * k;
    const wl = Math.hypot(wx, wy, wz);
    const along = wl > 0.02 ? ((a[0] - c[0]) * wx + (a[1] - c[1]) * wy + (a[2] - c[2]) * wz) / wl : 0;
    const tilt = 0.784 * Math.min(1, Math.abs(p.tilt)); // what the state byte decodes to in the shader
    const lift = reached ? Math.max(0, Math.min(1, p.lift)) : 0;
    const top = lift * (C.SH_LIFT + C.SH_PEEL * tilt * Math.max(0, along + 0.12)) - p.sink * (C.SH_SINK + 0.5 * C.SH_FRAG);
    return Math.min(top, 0.07);
  }

  /** Whether plate p's crack front has passed unit direction `a` (the shader's ra / rb, crBehind ≥ 0). */
  private reached(p: Plate3D | undefined, a: ArrayLike<number>) {
    return !!p && p.front > 0 && p.front - angle(p.origin, a) >= 0;
  }

  /**
   * The shell's relief at unit `a` (fraction of R), the CPU copy of shReliefAt as the vertex stage runs it: the clay floor in an open crack, the
   * plate's top past it (blended over the same ramp, so it is continuous across every border), the finger marks.
   * Sticky chips sit on it instead of sinking into it.
   */
  private reliefAt(a: ArrayLike<number>) {
    const h = this.imprint.heightAt(a), sh = this.shell;
    if (!sh.ready || !sh.ids || !sh.dist) return h;
    const i = dirToField(a[0], a[1], a[2], sh.fieldW, sh.fieldH);
    const pa = sh.plates[sh.ids[i * 2]], pb = sh.plates[sh.ids[i * 2 + 1]];
    if (!pa) return h;
    // as the vertex stage (shCrack, then shReliefAt with open × min(1, w / 3) and a 6-texel ramp): each side's gap
    // counts once its crack front has got here, without the fragment stage's tip thinning
    const ra = this.reached(pa, a), rb = this.reached(pb, a);
    const w = Math.max(ra && pa.cracked ? pa.gap : 0, rb && pb!.cracked ? pb!.gap : 0);
    const open = (ra && pa.cracked) || (rb && pb!.cracked) ? Math.min(1, w / 3) : 0;
    const d = sh.dist[i] / DIST_SCALE, t = Math.max(0, Math.min(1, (d - w) / 6)), sm = t * t * (3 - 2 * t);
    const top = this.plateTop(pa, a, ra);
    return h + (-C.SH_THICK * open) * (1 - sm) + top * sm;
  }

  /** The plastic memory changed: re-upload it as r − 1 (a few thousand half floats, on release only). */
  private uploadRest() {
    const toHalf = this.THREE.DataUtils.toHalfFloat, R = this.restR, H = this.restHalf;
    for (let v = 0; v < R.length; v++) H[v] = toHalf(R[v] - 1);
    this.texRest.needsUpdate = true;
  }

  /** Live silhouette radius in the screen plane, in world units (for walls and spacing). */
  /** The radius a touch ray is cast against: the ball's real surface (no spacing allowances). */
  touchRadius() {
    return this.R * this.extent * (1 + this.swell) * Math.max(1, this.sPerp);
  }

  visualRadius() {
    // the swell, the lip of clay, and the relief of broken plates standing proud near the silhouette
    const swell = 1 + 1.5 * this.swell + 0.5 * this.D * this.rimK() + (this.popped ? 0.05 : 0);
    return this.R * this.extent * swell * Math.max(1, this.sPerp) * (1 + 0.025 * Math.sin(Math.PI * Math.min(1, this.punchT)));
  }

  private measureExtent() {
    let m = 0;
    for (let v = 0; v < this.geo.nv; v++) if (this.restR[v] > m) m = this.restR[v];
    this.extent = Math.max(1, m);
  }

  /**
   * The deformed surface point along unit direction `a`, in mesh space: the vertex shader's smooth shape
   * (without the shell's few-percent relief), so chips and necks ride the dent and the swell.
   */
  surfaceAt(a: ArrayLike<number>, out: Float32Array, relief = true) {
    const ax = a[0], ay = a[1], az = a[2];
    let r = this.radiusAt(a) + (relief ? this.reliefAt(a) : 0);
    if (r < 0.45) r = 0.45;
    // the one-sided squash, as the vertex shader does it: the finger's side flattens, the far side stays put
    const sx = this.sq[0], sy = this.sq[1], sz = this.sq[2];
    const px = ax * r, py = ay * r, pz = az * r, along = px * sx + py * sy + pz * sz, al2 = (along + 1) * this.sPar - 1;
    out[0] = sx * al2 + (px - sx * along) * this.sPerp;
    out[1] = sy * al2 + (py - sy * along) * this.sPerp;
    out[2] = sz * al2 + (pz - sz * along) * this.sPerp;
    return out;
  }

  /** surfaceAt plus the outward normal there, by finite differences so it follows the dent and squash. */
  surfaceFrame(a: ArrayLike<number>, pos: Float32Array, nrm: Float32Array) {
    const ax = a[0], ay = a[1], az = a[2];
    this.surfaceAt(a, pos);
    // tangent frame (t1, t2, a) right-handed, so (q1 − pos) × (q2 − pos) points outward
    const ux = Math.abs(ay) < 0.9 ? 0 : 1, uy = 1 - ux, k = ux * ax + uy * ay;
    let t1x = ux - ax * k, t1y = uy - ay * k, t1z = -az * k;
    const tl = Math.hypot(t1x, t1y, t1z) || 1; t1x /= tl; t1y /= tl; t1z /= tl;
    const t2x = ay * t1z - az * t1y, t2y = az * t1x - ax * t1z, t2z = ax * t1y - ay * t1x;
    const e = 0.05, p1 = this.fp1, p2 = this.fp2;
    p1[0] = ax + t1x * e; p1[1] = ay + t1y * e; p1[2] = az + t1z * e;
    p2[0] = ax + t2x * e; p2[1] = ay + t2y * e; p2[2] = az + t2z * e;
    let l = Math.hypot(p1[0], p1[1], p1[2]); p1[0] /= l; p1[1] /= l; p1[2] /= l;
    l = Math.hypot(p2[0], p2[1], p2[2]); p2[0] /= l; p2[1] /= l; p2[2] /= l;
    // the normal of the smooth shape (the relief's steps would flip chips riding over a border)
    const p0 = this.fp0; this.surfaceAt(a, p0, false);
    this.surfaceAt(p1, p1, false); this.surfaceAt(p2, p2, false);
    const bx = p1[0] - p0[0], by = p1[1] - p0[1], bz = p1[2] - p0[2], cx = p2[0] - p0[0], cy = p2[1] - p0[1], cz = p2[2] - p0[2];
    nrm[0] = by * cz - bz * cy; nrm[1] = bz * cx - bx * cz; nrm[2] = bx * cy - by * cx;
    l = Math.hypot(nrm[0], nrm[1], nrm[2]);
    if (l < 1e-9 || (nrm[0] * ax + nrm[1] * ay + nrm[2] * az) / l < 0.2) { nrm[0] = ax; nrm[1] = ay; nrm[2] = az; } else { nrm[0] /= l; nrm[1] /= l; nrm[2] /= l; }
    return pos;
  }

  // ---- plate hooks for the sticky necks (sticky.ts reaches plates only through these and plate data) ----

  /**
   * Where a neck roots on plate `plateId` near unit direction `a`: point and outward normal in this mesh's
   * local (unit-radius) space; mesh.localToWorld gives world space.
   */
  plateAnchor(plateId: number, a: ArrayLike<number>, pos: Float32Array, nrm: Float32Array) {
    this.surfaceFrame(a, pos, nrm);
  }

  /**
   * Tears a chip out of plate `plateId` around `a`: outline `radii` (rad, evenly spaced in angle in the
   * t1/t2 frame), half-size `size`. Returns the chip's paint and leaves a clay hole where it was.
   */
  peelPlate(plateId: number, a: ArrayLike<number>, t1: ArrayLike<number>, t2: ArrayLike<number>, size: number, radii: number[]): HTMLCanvasElement {
    const paint = this.shell.cutChip(a, t1, t2, size);
    this.shell.addHole(a, t1, t2, radii);
    this.shell.plates[plateId].pulled = true;
    return paint;
  }

  // ---------- breaking ----------

  private key() { return "b" + this.id; }

  /** The fingertip's contact radius on this ball (rad): the same few millimetres of skin whatever the ball's size. */
  private contact(env: Env3D) {
    const rPx = this.R / env.pxToWorld;
    return Math.max(0.08, Math.min(0.6, this.pace.contactPx / Math.max(20, rPx)));
  }

  /**
   * A new damage stroke at the finger: which plates it reaches and how strongly (a soft disc the size of the
   * fingertip, the plate right under the finger always fully), and the damage each had before it.
   */
  private beginStroke(press: Press3D, now: number, env?: Env3D) {
    press.strokeDir = [press.dir[0], press.dir[1], press.dir[2]];
    press.strokeAt = now;
    this.near = [];
    this.radial = false;
    if (!this.shell.ready) return;
    const sig = env ? this.contact(env) : this.contactSig, d = press.dir;
    this.contactSig = sig;
    const under = this.shell.plateAt(d[0], d[1], d[2]);
    for (const p of this.shell.plates) {
      if (p.dmg >= DMG_CAP) continue;
      const a = Math.max(0, angle(p.dir, d) - 0.6 * p.rad);
      const w = p === under ? 1 : Math.exp(-(a * a) / (sig * sig));
      if (w < 0.03) continue;
      p.base = p.dmg;
      this.near.push({ p, w });
    }
  }
  private contactSig = 0.3;
  /** Presses a print into the clay (only where the shell is broken: softness per plate from IMP_SOFT). */
  private stamp(c: ArrayLike<number>, radius: number, o: StampOpts) {
    const ids = this.shell.ids; if (!ids) return;
    for (const p of this.shell.plates) this.impSoft[p.id] = IMP_SOFT[p.level];
    this.imprint.stamp(c, radius, ids, this.shell.fieldW, this.shell.fieldH, this.impSoft, o);
  }
  /** this stroke has already sent its long cracks running out from the finger */
  private radial = false;

  onPointerDown(press: Press3D, now: number, env: Env3D) {
    this.press = press; this.sleeping = false; this.pressCount++;
    this.fieldTicket?.bump(); // the finger is on this ball: its crack field jumps the queue
    // a quick press somewhere else while the last dent still springs back: that dent stays where it was (baked),
    // so nothing jumps across the ball when the new one starts
    if (this.D > 0.005 && angle(press.dir, this.dent) > 0.5 * this.sigma) {
      this.bakeLocal(1, true);
      this.D = Math.min(this.D, 0.01);
    }
    this.dent = [...press.dir];
    this.aimSquash(press);
    if (Math.abs(1 - this.sPar) < 0.004) this.sq = [...this.sqT]; // still squashed from the last press: ease, never snap
    this.D = Math.max(this.D, 0.01); this.Dt = this.D;
    if (this.D < 0.02) this.sigma = Math.max(0.14, Math.min(0.55, 1.35 * this.contact(env)));
    this.dragRun = 0;
    press.nextFlakeAt = now + 150;
    this.beginStroke(press, now, env);
    // the shell answers the touch straight away: a tiny dry tick from the wax before anything breaks
    if (this.shell.ready) env.audio.hairline(this.pitchMul * 1.1);
    this.sticky.begin(press.dir);
    if (this.stage >= 4) env.audio.wetStart(this.key() + ":wet");
  }

  /**
   * The squash axis: the finger pushes the ball toward the backdrop, so it is the screen-space press direction
   * (mostly into the screen), taken into the ball's frame. Worked out in world space so it never tilts with the
   * ball's orientation.
   */
  private aimSquash(press: Press3D) {
    const w = press.dirW, sx = w[0] * 1.6, sy = w[1] * 1.6, sz = w[2] * 0.7, sl = Math.hypot(sx, sy, sz) || 1;
    this.toLocal([sx / sl, sy / sl, sz / sl], this.sqT);
  }

  /**
   * Bakes `frac` of the live dent and its lip into the plastic memory where the dent is now (no squash, no volume
   * rescale: the release pays that back). Leaves a groove behind a dragging finger, keeps an abandoned dent.
   */
  private bakeLocal(frac: number, whole = false) {
    const dirs = this.geo.dirs, s = Math.max(1e-3, this.sigma), cut = Math.cos(Math.min(Math.PI, 3.5 * s)), rimK = this.rimK();
    const d = this.dent, D = this.D * frac;
    // what the soft limit lets the floor keep (`whole`: all of it, when the rest would otherwise vanish in a frame)
    const lim = whole ? 1 : Math.max(0, Math.min(1, (this.restAt(d) - 0.62) / 0.38));
    for (let v = 0; v < this.geo.nv; v++) {
      const ax = dirs[v * 3], ay = dirs[v * 3 + 1], az = dirs[v * 3 + 2];
      if (ax * d[0] + ay * d[1] + az * d[2] < cut) continue;
      const r0 = this.restR[v];
      // the same share of the dent and the lip everywhere (the soft limit at the dent's centre), so the live shape
      // plus the baked one is exactly what it was
      this.dentTerms(ax, ay, az);
      this.restR[v] = r0 * (1 - D * lim * this.tg + D * lim * rimK * this.tl);
    }
    this.D -= D * lim;
    this.uploadRest();
  }

  /** Damage done so far by this stroke at full weight: most of a press's share arrives in the first ~2 tau. */
  private strokeDamage(now: number) {
    const press = this.press!;
    const t = (now - press.strokeAt) / 1000;
    const sliding = press.isDrag ? 0.55 : 1; // a sliding finger scuffs rather than crushes
    return this.pace.dmgPerPress * sliding * press.pressure * (1 - Math.exp(-t / this.pace.dmgTau));
  }

  /**
   * Raises plate p to its damage level: hairline → crack → open → loose → crushed. Past that it keeps shattering
   * finer and sinking into the clay as long as presses keep coming; it never comes off. Collects what happened.
   */
  private levelUp(p: Plate3D, now: number, from: V3, ev: Events) {
    const d = p.dmg, sh = this.shell;
    const lv = d >= C.DMG_GONE + CRUSH_SPAN * 0.6 ? 6 : d >= C.DMG_GONE ? 5 : d >= C.DMG_LOOSE ? 4 : d >= C.DMG_OPEN ? 3 : d >= C.DMG_CRACK ? 2 : d >= C.DMG_HAIR ? 1 : 0;
    if (lv >= 5) {
      // crushed: the fragments part a little more and sink a little deeper with every bit of damage
      const u = Math.min(1, (d - C.DMG_GONE) / CRUSH_SPAN);
      sh.crush(p, 0.45 + 1.5 * u, 0.35 + 0.65 * u);
    } else if (lv >= this.pace.fineFrom) sh.crush(p, 0.001, 0); // the fragments' hairlines show before they part
    if (lv <= p.level) return;
    const toPlate = angle(p.dir, from);
    if (p.level < 1) { sh.hairline(p, from, toPlate + p.rad * 1.2); ev.hair++; }
    if (lv >= 2 && p.level < 2) {
      if (sh.crack(p, now, GAP_CRACK, 0.35, this.rng, from, toPlate + p.rad * 1.6 + 0.08)) this.crackedCount++;
      ev.crack++;
    }
    // the piece itself: an open plate rises a hair off the clay and tips; a loose one stands proud, tipped further;
    // a crushed one is pressed back down flat (its fragments sink, shReliefAt)
    if (lv >= 3 && p.level < 3) { p.gapTarget = Math.max(p.gapTarget, GAP_OPEN + 0.3 * this.rng()); sh.reachTo(p, from[0], from[1], from[2], Math.PI); p.tiltTarget *= 1.6; sh.raise(p, 0.35 + 0.15 * this.rng()); ev.open++; }
    if (lv >= 4 && p.level < 4) { p.gapTarget = Math.max(p.gapTarget, GAP_LOOSE + 0.4 * this.rng()); p.tiltTarget = Math.sign(p.tiltTarget || 1) * Math.min(1, Math.abs(p.tiltTarget) * 1.5 + 0.2); sh.raise(p, 0.8 + 0.2 * this.rng()); ev.loose++; }
    if (lv >= 5 && p.level < 5) { this.crushedCount++; ev.crushed++; p.tiltTarget *= 0.4; sh.raise(p, 0); }
    p.level = lv;
  }

  /**
   * The first real crack of a stroke sends 2–3 long cracks running out from the finger along the plate borders,
   * further than the finger itself reaches (wax is brittle: a crack keeps going). Plates on such a run crack near
   * the finger and craze further out.
   */
  private radialCracks(now: number, from: V3, ev: Events) {
    this.radial = true;
    const up = Math.abs(from[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    let t1x = up[1] * from[2] - up[2] * from[1], t1y = up[2] * from[0] - up[0] * from[2], t1z = up[0] * from[1] - up[1] * from[0];
    const l = Math.hypot(t1x, t1y, t1z) || 1; t1x /= l; t1y /= l; t1z /= l;
    const t2x = from[1] * t1z - from[2] * t1y, t2y = from[2] * t1x - from[0] * t1z, t2z = from[0] * t1y - from[1] * t1x;
    const runs = 2 + (this.rng() < 0.5 ? 1 : 0), a0 = this.rng() * Math.PI * 2;
    const reach = (2.3 + 0.8 * this.rng()) * this.contactSig;
    for (let r = 0; r < runs; r++) {
      const a = a0 + (r * Math.PI * 2) / runs + (this.rng() - 0.5) * 0.9;
      const ca = Math.cos(a), sa = Math.sin(a);
      for (const p of this.shell.plates) {
        if (p.level >= 2) continue;
        const dp = p.dir[0] * from[0] + p.dir[1] * from[1] + p.dir[2] * from[2];
        if (dp < 0.2) continue;
        // the plate's position in the finger's tangent plane: along the run, and to its side
        const u = p.dir[0] * t1x + p.dir[1] * t1y + p.dir[2] * t1z, v = p.dir[0] * t2x + p.dir[1] * t2y + p.dir[2] * t2z;
        const along = u * ca + v * sa, side = Math.abs(-u * sa + v * ca);
        if (along <= 0 || along > reach || side > 0.04 + 0.55 * p.rad) continue;
        const want = along < reach * 0.55 ? C.DMG_CRACK + 0.02 : C.DMG_HAIR + 0.04;
        if (p.dmg < want) { p.dmg = want; p.base = Math.max(p.base, want - 0.05); this.levelUp(p, now, from, ev); }
      }
    }
  }

  /** Applies the finger's damage for this frame and plays what broke. */
  private damageStep(now: number, env: Env3D) {
    const press = this.press!;
    if (!this.near.length) return;
    const g = this.strokeDamage(now);
    const ev: Events = { hair: 0, crack: 0, open: 0, loose: 0, crushed: 0 };
    let wSum = 0, sSum = 0;
    for (const n of this.near) {
      const p = n.p;
      const target = Math.min(DMG_CAP, p.base + (g * n.w) / p.thr);
      if (target > p.dmg) { p.dmg = target; this.levelUp(p, now, press.strokeDir, ev); }
      const s = SOFT[p.level];
      wSum += n.w * n.w; sSum += n.w * n.w * s;
    }
    this.soft = wSum > 0 ? sSum / wSum : 0;
    if (!this.radial && ev.crack > 0) this.radialCracks(now, press.strokeDir, ev);
    this.playEvents(ev, now, env);
  }

  /** Sound, haptics and wax dust for what broke this frame (several plates at once = one bigger crunch). */
  private playEvents(ev: Events, now: number, env: Env3D) {
    const a = env.audio, sub = !canVibrate, m = this.pitchMul, press = this.press!;
    const cracks = ev.crack + ev.open + ev.loose;
    if (ev.crushed) {
      // 바사삭: plates shattering into fragments under the finger, the biggest crunch of the toy
      vibrateHits(a.crack(Math.min(4, 2 + ev.crushed + (cracks ? 1 : 0)), 1, m * 0.92, sub), 18, a.hapticDelayMs());
      env.spawnCrumbs(this, press.dirW, Math.min(5, 1 + ev.crushed));
      env.onCrushed(this, ev.crushed);
      this.punchT = 0.0001;
      this.nextCrackSound = now + 60;
    } else if (cracks && now >= this.nextCrackSound) {
      const intensity = Math.min(1, (ev.crack + 0.8 * ev.open + 0.6 * ev.loose) / 3 + 0.3);
      vibrateHits(a.crack(cracks, intensity, m, sub), cracks > 2 ? 14 : 10, a.hapticDelayMs());
      this.nextCrackSound = now + 70;
    } else if (cracks) {
      this.nextCrackSound = Math.min(this.nextCrackSound, now + 30); // it will be heard on the next frame
    } else if (ev.hair && now >= this.nextHairSound) {
      a.hairline(m);
      this.nextHairSound = now + 55;
    }
    if (cracks || ev.crushed) {
      this.popped ||= ev.open + ev.loose + ev.crushed > 0;
      const crushed = this.crushedCount / Math.max(1, this.shell.plates.length);
      this.stage = crushed >= 0.4 ? 4 : this.popped ? 3 : 2;
    } else if (ev.hair && this.stage === 0) this.stage = 1;
  }

  advanceBeats(dt: number, now: number, env: Env3D) {
    const press = this.press, a = env.audio;
    const kk = 1 - Math.exp(-dt / C.SQUASH_TAU);
    if (press) {
      const hold = now - press.downAt;
      if (press.isDrag) {
        // the dent keeps up with the finger
        const d = this.dent, p = press.dir, d0x = d[0], d0y = d[1], d0z = d[2];
        d[0] += (p[0] - d[0]) * kk; d[1] += (p[1] - d[1]) * kk; d[2] += (p[2] - d[2]) * kk;
        const l = Math.hypot(d[0], d[1], d[2]) || 1; d[0] /= l; d[1] /= l; d[2] /= l;
        // dragged through soft clay, it leaves a groove behind it (deeper the softer the clay)…
        const step = Math.acos(Math.max(-1, Math.min(1, d0x * d[0] + d0y * d[1] + d0z * d[2])));
        this.dragRun += step;
        if (this.soft > 0.3 && this.dragRun > 0.3 * this.sigma) { this.dragRun = 0; this.bakeLocal(0.12 + 0.3 * this.soft); }
        // …and drives a mound of clay ahead of itself: the push aims along the dent's travel, strength from its speed
        if (step > 1e-5) {
          let mx = d[0] - d0x, my = d[1] - d0y, mz = d[2] - d0z;
          const md = mx * d[0] + my * d[1] + mz * d[2]; mx -= d[0] * md; my -= d[1] * md; mz -= d[2] * md;
          const ml = Math.hypot(mx, my, mz) || 1, str = Math.min(1, (step / dt) / 1.5) * Math.sqrt(this.soft) * 0.6;
          const pk = 1 - Math.exp(-dt / 0.12), p = this.push;
          p[0] += ((mx / ml) * str - p[0]) * pk; p[1] += ((my / ml) * str - p[1]) * pk; p[2] += ((mz / ml) * str - p[2]) * pk;
        }
        // a finger that slides on starts a new damage stroke where it now is, and smears the clay it passed
        const ang = angle(press.dir, press.strokeDir);
        if (ang > 0.7 * this.contactSig) {
          if (this.shell.ready) {
            const a0 = press.strokeDir, a1 = press.dir;
            const along: V3 = [a1[0] - a0[0], a1[1] - a0[1], a1[2] - a0[2]];
            const l2 = Math.hypot(along[0], along[1], along[2]) || 1; along[0] /= l2; along[1] /= l2; along[2] /= l2;
            const mid: V3 = [a0[0] + a1[0], a0[1] + a1[1], a0[2] + a1[2]];
            const lm = Math.hypot(mid[0], mid[1], mid[2]) || 1; mid[0] /= lm; mid[1] /= lm; mid[2] /= lm;
            const rad = 0.5 * this.contactSig;
            this.stamp(mid, rad, { depth: 0.004, rim: 0.6, ridges: 0, stretch: 1 + ang / (2 * rad), along });
          }
          this.beginStroke(press, now, env);
        }
      }
      this.aimSquash(press);
      this.damageStep(now, env);
      // hard wax gives a little under a finger; cracked shell more; bare clay sinks in, with a fingertip-sized dent
      const u = easeOut(hold / 140), s = this.soft;
      const Dt = (0.04 + 0.15 * s) * u;
      const sT = 1 - (0.0125 + 0.045 * s) * u;
      const sigT = Math.max(0.14, Math.min(0.55, (1.35 - 0.45 * s) * this.contactSig));
      this.sigma += (sigT - this.sigma) * kk;
      this.flat += (s - this.flat) * kk; // the pad flattens as the finger sinks into clay
      if (!press.isDrag) { const p = this.push; p[0] *= 1 - kk; p[1] *= 1 - kk; p[2] *= 1 - kk; }
      this.Dt = Dt; this.sParT = sT;
      const D0 = this.D;
      this.D += (Dt - this.D) * kk; this.sPar += (sT - this.sPar) * kk;
      // the rest of the ball swells with the push (the clay has to go somewhere)
      this.swell += (0.18 * Dt - this.swell) * kk;
      const dD = this.D - D0;
      if (dD > 0) {
        if (s < 0.25) a.squeeze(this.key(), dD, this.pitchMul, this.D); // 뽀드득: intact wax creaking under the finger
        else a.squish(Math.min(1, dD * 12 * s), this.pitchMul); // 꾹: the finger sinking into clay
      }
      if (s > 0.5) {
        if (this.stage < 4) a.wetStart(this.key() + ":wet");
        const moving = press.isDrag && now - press.lastMoveAt < 60;
        a.wetDrive(this.key() + ":wet", moving ? press.speed : 0, Math.min(1, press.moved / 150));
      }
      this.ooze = Math.max(this.ooze, 0.5 * s);
    } else {
      const kr = 1 - Math.exp(-dt / C.RELEASE_TAU);
      this.D += (0 - this.D) * kr; this.sPar += (1 - this.sPar) * kr;
      const pp = this.pendingPrint;
      if (pp) {
        this.stamp(pp.c, pp.radius, { depth: pp.depth / PRINT_STEPS, rim: 0.6, ridges: 0.22 });
        if (--pp.left <= 0) this.pendingPrint = null;
      }
      this.swell *= Math.exp(-dt / 0.35); // the swell settles a little slower than the dent springs back
      const pe = Math.exp(-dt / C.RELEASE_TAU), p = this.push; p[0] *= pe; p[1] *= pe; p[2] *= pe;
      if (Math.abs(this.D) < 0.002 && Math.abs(this.sPar - 1) < 0.002 && this.swell < 0.001 && !this.pendingPrint && this.settle++ > 10 && !this.sticky.busy && !this.shell.animating) { this.D = 0; this.sPar = 1; this.swell = 0; this.sleeping = true; }
    }
    // the squash axis eases toward where the finger is (never snaps on a quick re-press)
    const q = this.sq, t = this.sqT;
    q[0] += (t[0] - q[0]) * kk; q[1] += (t[1] - q[1]) * kk; q[2] += (t[2] - q[2]) * kk;
    const ql = Math.hypot(q[0], q[1], q[2]) || 1; q[0] /= ql; q[1] /= ql; q[2] /= ql;
    this.sPerp = 1 + 0.5 * (1 - this.sPar);
    const ran = this.shell.step(dt);
    if (ran > 0) a.crackleRun(this.key(), ran, this.pitchMul, false);
    this.sticky.step(dt, now, env);
    if (this.punchT > 0) { this.punchT += dt / (C.PUNCH_MS / 1000); if (this.punchT >= 1) this.punchT = 0; this.mesh.scale.setScalar(this.R * (1 + 0.025 * Math.sin(Math.PI * Math.min(1, this.punchT)))); }
  }

  onPointerUp(now: number, env: Env3D) {
    const press = this.press; if (!press) return;
    const hold = now - press.downAt;
    if (hold < C.HOLD_CRACK_MS && this.stage < 2) env.audio.tick(1500 * this.pitchMul, 20, -18);
    // the finger's print stays in the clay where the shell is broken: a flat-bottomed cup, a narrow lip, ridges —
    // pressed in over the next few frames, as the dent springs back, so nothing pops when the finger lifts
    if (this.shell.ready && this.D > 0.02) this.pendingPrint = { c: [...this.dent], radius: 0.65 * this.contactSig, depth: 0.018 + 0.09 * this.D, left: PRINT_STEPS };
    // bake: the clay keeps part of the dent and of the lip it pushed up (bare clay much more than hard wax) — the
    // same terms the live shape has, so nothing jumps when the finger lifts — with soft limits instead of clamps;
    // then the shape is rescaled to the ball's original volume, so what went in comes out as a slight, lasting swell
    const keep = 0.05 + 0.7 * this.soft, dirs = this.geo.dirs;
    const rimK = this.rimK(), W = this.geo.segW;
    // where the clay is already pressed deep the bake keeps less (its soft limit): the live dent gives up only what
    // was actually kept, so the dent's floor stays exactly where it was when the finger lifts
    const keepEff = keep * Math.max(0, Math.min(1, (this.restAt(this.dent) - 0.62) / 0.38));
    let after = 0;
    for (let v = 0; v < this.geo.nv; v++) {
      const ax = dirs[v * 3], ay = dirs[v * 3 + 1], az = dirs[v * 3 + 2], r0 = this.restR[v];
      this.dentTerms(ax, ay, az);
      // the share kept is keepEff everywhere (the live dent gives back exactly that much), less only where this
      // vertex is pressed deeper than the dent's centre
      const kd = Math.min(keepEff, keep * Math.max(0, Math.min(1, (r0 - 0.62) / 0.38)));
      const r1 = r0 * (1 - kd * this.D * this.tg + keepEff * this.D * rimK * this.tl);
      this.restR[v] = r1;
      // volume ∝ Σ r³ · (area of the grid vertex) ∝ Σ r³ sinθ; the seam column is a duplicate, so it is left out
      if (v % (W + 1) !== W) after += r1 * r1 * r1 * Math.sqrt(Math.max(0, 1 - ay * ay));
    }
    if (after > 0) {
      const k = Math.cbrt(this.geo.v0 / after);
      for (let v = 0; v < this.geo.nv; v++) this.restR[v] *= k;
    }
    this.uploadRest();
    this.measureExtent();
    this.D *= 1 - keepEff; this.Dt = 0; this.sParT = 1;
    this.lastReleaseAt = now; this.press = null; this.settle = 0; this.near = [];
    this.sticky.release(env);
    if (press.isDrag && now - press.lastMoveAt < 80) this.flick(press.vx, press.vy, env.pxToWorld);
    env.audio.release(this.key());
  }
}
